// types
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { LenderProbabilityPayload } from '../types/lenderProbabiltyTypes';
import type { QualificationId } from '@makemydeal/dr-shared-types';

// selectors
import * as vehicleSelectors from '../selectors/vehicleSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as calculationDetailsSelectors from '../selectors/calculationDetailsSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { getCoBuyerCommonConsumerId, getShopperCommonConsumerId } from '../selectors/offerRedux';
import { getSoftPullConsentProvided } from '../selectors/offerInfoSelectors';

export const getLenderProbabilitiesPayload = (state: StateTree): LenderProbabilityPayload => {
    // Deal selectors
    const dealXgId = offerReduxSelectors.getDealXgId(state);

    // Vehicle selectors
    const vin = vehicleSelectors.getVehicleVin(state);
    const make = vehicleSelectors.getVehicleMake(state);
    const model = vehicleSelectors.getVehicleModel(state);
    const trim = vehicleSelectors.getVehicleTrim(state);
    const year = vehicleSelectors.getVehicleYear(state);
    const condition = vehicleSelectors.getVehicleCondition(state);
    const exteriorColor = vehicleSelectors.getVehicleExteriorColor(state);
    const interiorColor = vehicleSelectors.getVehicleInteriorColor(state);
    const stockNumber = vehicleSelectors.getStockNumber(state);
    const bodyStyle = vehicleSelectors.getBodyStyle(state);
    const driveLine = vehicleSelectors.getDriveLine(state);
    const listedPrice = vehicleSelectors.getListedPrice(state);

    // Customer selectors
    const shopperInfo = offerReduxSelectors.getShopperInfo(state);
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    const cashDownAmount = offerReduxSelectors.getDownPayment(state, offerType) ?? 0;
    const ficoScore = offerReduxSelectors.getManualCreditScore(state);

    // Soft Pull Consent selectors
    const softPullConsentProvided = getSoftPullConsentProvided(state);

    // Deal Term(s) selectors
    const term = offerReduxSelectors.getSelectedTerm(state);
    const termMonths = term?.months;

    // Dealer Info selectors
    const accelerateDealerId = dealerSelectors.getDealerId(state);
    const commonOrgDealerId = offerReduxSelectors.getCommonOrgId(state);
    const sourcePartnerDealerId = calculationDetailsSelectors.getPartnerDealerId(state);
    const sourcePartnerId = calculationDetailsSelectors.getPartnerCode(state)?.toString();

    return {
        dealXgId,
        vehicle: {
            vin,
            make,
            model,
            trim,
            year,
            condition,
            bodyStyle,
            listedPrice,
            exteriorColor,
            interiorColor,
            stockNumber,
            driveLine
        },
        selfReportedCredit: {
            ficoScoreV8: ficoScore,
            state: shopperInfo?.state
        },
        softPullConsentProvided,
        payment: {
            months: termMonths,
            netCashDown: cashDownAmount
        },
        dealer: {
            sourcePartnerDealerId,
            sourcePartnerId,
            accelerateDealerId,
            commonOrgDealerId
        }
    };
};

export const formatQualificationIds = (qualificationIds: { type: string; value: string }[] | undefined, state: StateTree) => {
    if (!qualificationIds || qualificationIds.length === 0) return [];
    return qualificationIds.reduce<QualificationId[]>((acc, qualification) => {
        const commonConsumer =
            qualification.type === 'current' ? getShopperCommonConsumerId(state) : getCoBuyerCommonConsumerId(state);
        if (commonConsumer) {
            acc.push({
                name: `${commonConsumer}_QualificationID`,
                value: qualification.value
            });
        }
        return acc;
    }, []);
};
